import { message } from "antd";
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";

import type { RolePermOpenParams } from "@/components/RoleDetailModal";
import { RoleDetailModal } from "@/components/RoleDetailModal";
import { AccountDetailModal } from "@/pages/Accounts/components/AccountDetail";
import { AgentDetailModal } from "@/pages/Agents/components/AgentDetailModal";
import { EmployeeDetailModal } from "@/pages/Employees/components/EmployeeDetailModal";
import { EntityDetailModal } from "@/pages/EntitiesPage/components/EntityDetailModal";
import { StoreDetailModal } from "@/pages/Stores/components/StoreDetailModal";

import { RelatedDetailContext } from "./detailNavigationContext";
import type {
  RelatedDetailContextValue,
  RelatedDetailStackItem,
  RelatedEntityDetailKind,
  StoreListHandler,
} from "./types";

/** 空 ID 时的提示文案 */
const EMPTY_ID_MESSAGE: Record<RelatedEntityDetailKind, string> = {
  agent: "未关联代理",
  store: "未关联门店",
  entity: "未关联工商实体",
  account: "未关联账号",
  employee: "未关联员工",
};

/** 校验 ID 后入栈打开详情 */
const pushDetail = (
  kind: RelatedEntityDetailKind,
  id: string | null | undefined,
  setStack: Dispatch<SetStateAction<RelatedDetailStackItem[]>>,
) => {
  const trimmed = id?.trim();
  if (!trimmed) {
    message.warning(EMPTY_ID_MESSAGE[kind]);
    return;
  }
  setStack((prev) => [...prev, { kind, id: trimmed }]);
};

/** 渲染栈顶详情 Modal */
const RelatedDetailHost = ({
  stack,
  onClose,
  nav,
  onOpenStoreListFallback,
}: {
  stack: RelatedDetailStackItem[];
  onClose: () => void;
  nav: RelatedDetailContextValue;
  onOpenStoreListFallback: (agentId: string) => void;
}) => {
  const top = stack[stack.length - 1];
  if (!top) return null;

  switch (top.kind) {
    case "store":
      return (
        <StoreDetailModal
          open
          shopId={top.id}
          onClose={onClose}
          onOpenAgent={nav.openAgent}
          onOpenStore={nav.openStore}
          onOpenEntity={nav.openEntity}
          onOpenAccount={nav.openAccount}
        />
      );
    case "agent":
      return (
        <AgentDetailModal
          open
          agentId={top.id}
          onClose={onClose}
          onOpenAccount={nav.openAccount}
          onOpenEntity={nav.openEntity}
          onOpenStoreList={onOpenStoreListFallback}
        />
      );
    case "entity":
      return (
        <EntityDetailModal
          open
          entityId={top.id}
          onClose={onClose}
          showFooter={false}
          onClickShop={nav.openStore}
          onClickAgent={nav.openAgent}
          onClickAccount={nav.openAccount}
        />
      );
    case "account":
      return <AccountDetailModal open onClose={onClose} id={top.id} />;
    case "employee":
      return (
        <EmployeeDetailModal
          open
          employeeId={top.id}
          onClose={onClose}
          onOpenRole={nav.openRolePerm}
        />
      );
    case "role":
      return <RoleDetailModal open roleName={top.roleName} source={top.source} onClose={onClose} />;
    default:
      return null;
  }
};

/** 跨实体详情 Provider：包裹业务页并挂载全局详情 Host */
export const RelatedDetailProvider = ({ children }: { children: ReactNode }) => {
  // 详情栈，支持 A → B 逐层打开
  const [stack, setStack] = useState<RelatedDetailStackItem[]>([]);
  // 代理页注册的「下辖门店列表」回调
  const storeListHandlerRef = useRef<StoreListHandler | null>(null);

  /** 关闭栈顶一层 */
  const close = useCallback(() => {
    setStack((prev) => prev.slice(0, -1));
  }, []);

  /** 清空详情栈 */
  const closeAll = useCallback(() => {
    setStack([]);
  }, []);

  /** 打开角色权限详情（入栈） */
  const openRolePerm = useCallback((params: RolePermOpenParams) => {
    setStack((prev) => [
      ...prev,
      { kind: "role", roleName: params.roleName, source: params.source },
    ]);
  }, []);

  /** 代理页注册/注销下辖门店列表打开器 */
  const registerStoreListHandler = useCallback((handler: StoreListHandler | null) => {
    storeListHandlerRef.current = handler;
  }, []);

  /** 下辖门店：优先走代理页注册回调，否则提示 */
  const onOpenStoreListFallback = useCallback((agentId: string) => {
    if (storeListHandlerRef.current) {
      storeListHandlerRef.current(agentId);
      return;
    }
    message.info("下辖门店列表请从代理管理页查看");
  }, []);

  const nav = useMemo<RelatedDetailContextValue>(
    () => ({
      openAgent: (id) => pushDetail("agent", id, setStack),
      openStore: (id) => pushDetail("store", id, setStack),
      openEntity: (id) => pushDetail("entity", id, setStack),
      openAccount: (id) => pushDetail("account", id, setStack),
      openEmployee: (id) => pushDetail("employee", id, setStack),
      openRolePerm,
      close,
      closeAll,
      registerStoreListHandler,
    }),
    [close, closeAll, openRolePerm, registerStoreListHandler],
  );

  return (
    <RelatedDetailContext.Provider value={nav}>
      {children}
      <RelatedDetailHost
        stack={stack}
        onClose={close}
        nav={nav}
        onOpenStoreListFallback={onOpenStoreListFallback}
      />
    </RelatedDetailContext.Provider>
  );
};
